import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";

const HERO_TEXTURE =
  "https://images.unsplash.com/photo-1671494685095-68b5c92963e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxkYXJrJTIwcmVkJTIwY3JpbXNvbiUyMHBhaW50JTIwdGV4dHVyZSUyMGJhY2tncm91bmR8ZW58MHx8fDE3OTA3Mjk5NDZ8MA&ixlib=rb-4.1.0&q=85";

function bez(p0, p1, p2, p3, t) {
  const u = 1 - t;
  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
}

function drawBrush(ctx, x, y, angle, dpr) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.scale(dpr, dpr);
  ctx.shadowColor = "rgba(229,9,20,0.65)";
  ctx.shadowBlur = 20;
  // bristles (tip at 0,0)
  const bg = ctx.createLinearGradient(-52, 0, 0, 0);
  bg.addColorStop(0, "#5c040a");
  bg.addColorStop(0.55, "#b00710");
  bg.addColorStop(1, "#ff202e");
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(-46, -10);
  ctx.quadraticCurveTo(-52, 0, -46, 10);
  ctx.closePath();
  ctx.fill();
  // ferrule
  const fg = ctx.createLinearGradient(0, -11, 0, 11);
  fg.addColorStop(0, "#e8edf3");
  fg.addColorStop(0.5, "#8b95a5");
  fg.addColorStop(1, "#3c4350");
  ctx.shadowBlur = 0;
  ctx.fillStyle = fg;
  ctx.fillRect(-76, -10, 26, 20);
  // handle
  const hg = ctx.createLinearGradient(0, -7, 0, 7);
  hg.addColorStop(0, "#33333c");
  hg.addColorStop(0.45, "#121216");
  hg.addColorStop(1, "#050506");
  ctx.fillStyle = hg;
  ctx.beginPath();
  ctx.roundRect(-190, -7, 116, 14, 7);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.16)";
  ctx.fillRect(-186, -5, 108, 2);
  ctx.restore();
}

export default function PaintHero() {
  const canvasRef = useRef(null);
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [canvasGone, setCanvasGone] = useState(false);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });
  const logoX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const logoY = useTransform(sy, [-0.5, 0.5], [-10, 10]);
  const logoRX = useTransform(sy, [-0.5, 0.5], [5, -5]);
  const logoRY = useTransform(sx, [-0.5, 0.5], [-7, 7]);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const small = window.innerWidth < 640;
    if (reduce || small) {
      setRevealed(true);
      setCanvasGone(true);
      return;
    }
    const canvas = canvasRef.current;
    const section = sectionRef.current;
    if (!canvas || !section) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = section.clientWidth;
    const h = section.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.fillStyle = "#070709";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const strokes = [
      { p0: { x: -0.08 * w, y: 0.92 * h }, p1: { x: 0.28 * w, y: 0.78 * h }, p2: { x: 0.5 * w, y: 0.28 * h }, p3: { x: 1.1 * w, y: 0.1 * h }, w: Math.min(w, h) * 0.16, start: 0, dur: 1.7 },
      { p0: { x: 0.06 * w, y: 0.1 * h }, p1: { x: 0.2 * w, y: 0.16 * h }, p2: { x: 0.34 * w, y: 0.22 * h }, p3: { x: 0.52 * w, y: 0.34 * h }, w: Math.min(w, h) * 0.07, start: 1.35, dur: 0.7 },
      { p0: { x: 0.62 * w, y: 1.02 * h }, p1: { x: 0.74 * w, y: 0.94 * h }, p2: { x: 0.9 * w, y: 0.88 * h }, p3: { x: 1.06 * w, y: 0.74 * h }, w: Math.min(w, h) * 0.09, start: 1.6, dur: 0.7 },
    ];
    const total = 2.5;
    let raf;
    let revealedFired = false;
    const t0 = performance.now();
    const last = strokes.map(() => 0);

    const frame = (now) => {
      const t = (now - t0) / 1000;
      strokes.forEach((s, i) => {
        let p = Math.min(Math.max((t - s.start) / s.dur, 0), 1);
        if (p <= last[i]) return;
        const eased = 1 - Math.pow(1 - p, 3);
        const easedLast = 1 - Math.pow(1 - last[i], 3);
        const steps = Math.max(2, Math.ceil((eased - easedLast) * 90));
        ctx.globalCompositeOperation = "destination-out";
        for (let k = 0; k <= steps; k++) {
          const tt = easedLast + ((eased - easedLast) * k) / steps;
          const pos = bez(s.p0, s.p1, s.p2, s.p3, tt);
          for (let j = 0; j < 6; j++) {
            const jr = s.w * (0.45 + Math.random() * 0.6) * dpr;
            const jx = pos.x * dpr + (Math.random() - 0.5) * s.w * 0.7 * dpr;
            const jy = pos.y * dpr + (Math.random() - 0.5) * s.w * 0.7 * dpr;
            ctx.globalAlpha = 0.85;
            ctx.beginPath();
            ctx.arc(jx, jy, jr, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.globalAlpha = 1;
        // splatter at tip
        if (Math.random() < 0.5 && p < 1) {
          const tip = bez(s.p0, s.p1, s.p2, s.p3, eased);
          ctx.globalCompositeOperation = "source-over";
          for (let j = 0; j < 4; j++) {
            const sr = (1 + Math.random() * 2.6) * dpr;
            const sx0 = tip.x * dpr + (Math.random() - 0.5) * s.w * 2.2 * dpr;
            const sy0 = tip.y * dpr + (Math.random() - 0.5) * s.w * 2.2 * dpr;
            ctx.fillStyle = Math.random() < 0.7 ? "rgba(229,9,20,0.55)" : "rgba(203,213,225,0.3)";
            ctx.beginPath();
            ctx.arc(sx0, sy0, sr, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        // brush sprite at tip
        if (p > 0.01 && p < 1) {
          const tip = bez(s.p0, s.p1, s.p2, s.p3, eased);
          const ahead = bez(s.p0, s.p1, s.p2, s.p3, Math.min(eased + 0.01, 1));
          const angle = Math.atan2(ahead.y - tip.y, ahead.x - tip.x);
          ctx.globalCompositeOperation = "source-over";
          ctx.save();
          ctx.beginPath();
          ctx.rect(0, 0, canvas.width, canvas.height);
          ctx.clip();
          drawBrush(ctx, tip.x * dpr, tip.y * dpr, angle, dpr);
          ctx.restore();
        }
        last[i] = p;
      });
      if (!revealedFired && t > 0.9) {
        revealedFired = true;
        setRevealed(true);
      }
      if (t < total) {
        raf = requestAnimationFrame(frame);
      } else {
        canvas.style.transition = "opacity 0.7s ease";
        canvas.style.opacity = "0";
        setRevealed(true);
        setTimeout(() => setCanvasGone(true), 750);
      }
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onMouseMove = (e) => {
    const r = sectionRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const lines = [
    { text: "WE CREATE", cls: "text-white" },
    { text: "WHAT YOUR BRAND", cls: "text-transparent", outline: true },
    { text: "NEEDS.", cls: "text-arsenal" },
  ];

  return (
    <section
      ref={sectionRef}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-[#070709] grain"
    >
      {/* painted background (revealed by the brush) */}
      <div className="absolute inset-0" aria-hidden>
        <img src={HERO_TEXTURE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070709] via-[#070709]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070709] via-transparent to-[#070709]/60" />
        <div className="absolute -left-24 top-1/3 h-96 w-96 rounded-full bg-arsenal/15 blur-[130px] animate-pulse-glow" />
        {[...Array(5)].map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-arsenal/50 animate-drift-slow"
            style={{
              width: 3 + (i % 3) * 2,
              height: 3 + (i % 3) * 2,
              left: `${12 + i * 18}%`,
              top: `${22 + ((i * 23) % 55)}%`,
              animationDelay: `${i * 1.4}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-20 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={revealed ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.35em] text-arsenal mb-6"
            data-testid="hero-eyebrow"
          >
            ARSENAL ARTISTS — CREATIVE STUDIO
          </motion.p>
          <h1 className="font-display font-black tracking-tight leading-[0.95] text-[13.5vw] sm:text-6xl lg:text-7xl xl:text-[5.2rem]">
            {lines.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${l.cls}`}
                  style={l.outline ? { WebkitTextStroke: "1.5px rgba(248,250,252,0.9)" } : undefined}
                  initial={{ y: "112%" }}
                  animate={revealed ? { y: "0%" } : {}}
                  transition={{ duration: 0.75, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`hero-headline-line-${i + 1}`}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={revealed ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.65 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
            data-testid="hero-subcopy"
          >
            From logos and graphics to complete branding and video production, Arsenal Artists provides
            creative work for creators, communities and businesses.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={revealed ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/start-a-project"
              data-testid="hero-start-project-btn"
              className="clip-btn group inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-sm px-8 py-4 transition-colors duration-200 shadow-[0_0_35px_rgba(229,9,20,0.35)]"
            >
              START A PROJECT
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/portfolio"
              data-testid="hero-view-work-btn"
              className="clip-btn inline-flex items-center gap-2 border border-white/20 hover:border-arsenal hover:text-arsenal text-white font-display font-bold tracking-widest text-sm px-8 py-4 transition-colors duration-200"
            >
              VIEW OUR WORK
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.12 }}
          animate={revealed ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:flex justify-center [perspective:900px]"
        >
          <motion.img
            src="/arsenal-artists-logo.png"
            alt="The Arsenal Artists emblem — metallic monogram with paintbrush, pen and red paint"
            data-testid="hero-logo"
            style={{ x: logoX, y: logoY, rotateX: logoRX, rotateY: logoRY }}
            className="w-[26rem] max-w-full object-contain drop-shadow-[0_0_60px_rgba(229,9,20,0.35)] animate-drift-slow"
            loading="eager"
          />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={revealed ? { opacity: 1 } : {}}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-slate-500"
        aria-hidden
      >
        <ChevronDown className="h-5 w-5 animate-bounce" />
      </motion.div>

      {!canvasGone && (
        <canvas ref={canvasRef} data-testid="hero-paint-canvas" className="absolute inset-0 z-20 pointer-events-none" aria-hidden />
      )}
    </section>
  );
}
